import React from 'react';
import { Inbox, LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

interface EmptyStateProps {
  /** Font Awesome class string (legacy) or Lucide icon component */
  icon?: string | LucideIcon;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  };
  className?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon = Inbox,
  title,
  description,
  action,
  className = '',
}) => {
  // Render icon - support both Font Awesome (string) and Lucide (component)
  const renderIcon = () => {
    if (typeof icon === 'string') {
      // Font Awesome legacy support
      return (
        <i
          className={icon}
          style={{
            fontSize: '2rem',
            color: 'var(--tv-text-tertiary)'
          }}
        />
      );
    }
    // Lucide React icon
    const IconComponent = icon;
    return <IconComponent className="w-8 h-8 text-text-muted" />;
  };

  return (
    <div className={cn(
      'flex flex-col items-center justify-center',
      'py-8 px-3 sm:py-12 sm:px-6',
      'min-h-[160px] sm:min-h-[200px]',
      className
    )}>
      <div
        className="p-4 rounded-full mb-4 bg-surface-container"
        style={typeof icon !== 'string' ? undefined : { backgroundColor: 'var(--tv-bg-tertiary)' }}
      >
        {renderIcon()}
      </div>

      <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-2 text-center px-1">
        {title}
      </h3>

      {description && (
        <p className="text-xs sm:text-sm text-text-muted text-center mb-6 max-w-[280px] sm:max-w-[400px] px-2">
          {description}
        </p>
      )}

      {action && (
        <button
          className={cn(
            'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors',
            'min-h-[44px] min-w-[120px]',
            action.variant === 'secondary'
              ? 'bg-surface-container text-text-primary border border-border-subtle hover:bg-surface-container-low'
              : 'bg-accent-primary text-[#3c2f00] hover:opacity-90'
          )}
          onClick={action.onClick}
        >
          {action.label}
        </button>
      )}
    </div>
  );
};

export default EmptyState;